import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Star, AlertTriangle, Info, Download, Check } from 'lucide-react';
import { instances, type TenantInput, type TenantState } from '../api';

/**
 * Mit welchen Mandanten eine Instanz startet — und wer dort Administrator ist.
 *
 * Warum das hier steht und nicht in der Instanz eingerichtet wird: nach einer
 * frischen Installation sieht ein Administrator fast nichts, weil Rechte
 * ausschließlich aus Gruppen kommen und eine frische Installation keine Gruppe
 * hat. Wer das nicht weiß, macht den Kunden zum Superadmin — womit er Zugriff
 * auf alle Mandanten bekommt, um ein Anzeigeproblem in seinem eigenen zu lösen.
 * Deshalb beschreibt der PC den Startzustand vollständig und die App legt ihn an.
 */

interface Row extends TenantInput {
  /** Nur für React. Neue Zeilen haben keine ID aus der Datenbank. */
  key: string;
  code: string;
  name: string;
  isPrimary: boolean;
}

let nextKey = 0;

/**
 * Welche Module die Instanz anbietet — was der Kunde gebucht hat.
 *
 * Nichts ausgewählt heißt ausdrücklich **nicht** „nichts freigeschaltet", sondern
 * „nicht vorgeben": die Instanz behält, was ihr Abbild mitbringt. Das steht auch
 * so in der Oberfläche, weil der Unterschied sonst gefährlich ist — wer hier
 * versehentlich alles abwählt und speichert, würde sonst eine laufende
 * Installation leerräumen.
 */
function ModulFreischaltung({ instanceId, state }: { instanceId: string; state: TenantState }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [auswahl, setAuswahl] = useState<string[]>(state.enabledModules);
  const [dirty, setDirty] = useState(false);
  const [fehler, setFehler] = useState('');

  useEffect(() => {
    if (!dirty) setAuswahl(state.enabledModules);
  }, [state.enabledModules, dirty]);

  const save = useMutation({
    mutationFn: () => instances.setModules(instanceId, auswahl),
    onSuccess: (neu) => {
      setDirty(false);
      setFehler('');
      qc.setQueryData(['instances', instanceId, 'tenants'], neu);
    },
    onError: (err: unknown) => {
      setFehler(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Speichern fehlgeschlagen.',
      );
    },
  });

  if (!state.availableModules.length) return null;

  const um = (code: string) => {
    setDirty(true);
    setAuswahl((a) => (a.includes(code) ? a.filter((c) => c !== code) : [...a, code]));
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-surface">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-700">{t('tenants.enabledModules')}</h3>
          <p className="mt-0.5 text-xs text-slate-400">
            Was der Kunde gebucht hat. Untermodule folgen ihrem Elternmodul.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => { setDirty(true); setAuswahl(state.availableModules); }}
            className="rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
          >
            alle
          </button>
          <button
            type="button"
            onClick={() => { setDirty(true); setAuswahl([]); }}
            className="rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
          >
            keine
          </button>
          {dirty && <span className="text-xs text-amber-600">{t('common.unsaved')}</span>}
          <button
            type="button"
            disabled={save.isPending}
            onClick={() => save.mutate()}
            className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs text-white hover:bg-brand-700 disabled:opacity-60"
          >
            {save.isPending ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
      </div>

      {!auswahl.length && (
        <div className="border-b border-amber-100 bg-amber-50 px-5 py-2.5 text-xs text-amber-800">
          Nichts ausgewählt heißt <strong>{t('tenants.doNotPreset')}</strong> — die Instanz behält, was ihr
          Abbild mitbringt. Es heißt nicht „alle Module aus".
        </div>
      )}

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 px-5 py-4 sm:grid-cols-3">
        {state.availableModules.map((code) => (
          <label key={code} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={auswahl.includes(code)} onChange={() => um(code)} />
            <code className="font-mono text-xs">{code}</code>
          </label>
        ))}
      </div>

      {fehler && (
        <div className="border-t border-red-100 bg-red-50 px-5 py-2 text-sm text-red-700">{fehler}</div>
      )}
      <div className="border-t border-slate-100 px-5 py-2.5 text-xs text-slate-400">
        {auswahl.length} von {state.availableModules.length} gewählt · wirkt beim nächsten Ausrollen
      </div>
    </div>
  );
}

function toRows(state: TenantState | undefined): Row[] {
  return (state?.tenants ?? []).map((t) => ({
    key: t.id,
    code: t.code,
    name: t.name,
    isPrimary: t.isPrimary,
  }));
}

export default function TenantsPanel({ instanceId }: { instanceId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [rows, setRows] = useState<Row[]>([]);
  const [retire, setRetire] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['instances', instanceId, 'tenants'],
    queryFn: () => instances.getTenants(instanceId),
  });

  // Nur übernehmen, solange nichts Ungespeichertes im Formular steht — sonst
  // würde ein Refetch im Hintergrund die Eingabe wegräumen.
  useEffect(() => {
    if (dirty) return;
    setRows(toRows(data));
    setRetire(data?.retireUnlisted ?? false);
  }, [data, dirty]);

  const save = useMutation({
    mutationFn: () =>
      instances.setTenants(
        instanceId,
        rows.map((r) => ({ code: r.code, name: r.name, isPrimary: r.isPrimary })),
        retire,
      ),
    onSuccess: (state) => {
      setDirty(false);
      setError('');
      setLesegrund(null);
      setGelesen(null);
      qc.setQueryData(['instances', instanceId, 'tenants'], state);
      setRows(toRows(state));
      // Die Zahl aus der Antwort, nicht aus dem Formular: der Server wirft
      // namenlose Zeilen weg, und dann stimmte die Meldung nicht mit dem
      // ueberein, was jetzt dasteht.
      setGespeichert({ anzahl: state.tenants.length, retire });
    },
    onError: (err: unknown) => {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Speichern fehlgeschlagen.',
      );
    },
  });

  // Alle Hooks stehen vor den fruehen Ausstiegen unten.
  //
  // Hier gelernt: standen sie dahinter, sah React beim Ladezustand weniger
  // Hooks als beim naechsten Rendern, warf, und die Mandantenseite blieb weiss.
  // Weder TypeScript noch der Build sehen das -- es faellt erst im Browser auf.
  /**
   * Die Mandanten holen, die in der laufenden Instanz wirklich stehen.
   *
   * Der PC hält nur den Startzustand. Auf den Code kommt es an: ojfm gleicht
   * über `ON CONFLICT (code) DO UPDATE SET name` — mit dem richtigen Code wird
   * umbenannt, mit einem falschen ein zweiter Mandant angelegt, und das sieht
   * aus wie ein Erfolg.
   *
   * Ersetzt nur das Formular, speichert nichts. Wer es sich anders überlegt,
   * lädt die Seite neu.
   */
  /**
   * Was der letzte Speichervorgang ergeben hat.
   *
   * Vorher verschwand beim Speichern nur der Hinweis „ungespeichert" — und
   * damit war der einzige Unterschied zwischen „gespeichert" und „Knopf nicht
   * getroffen" ein verschwundenes Wort. Wichtiger noch ist die zweite Hälfte:
   * die Liste ist der *Sollzustand*. In der laufenden Instanz ändert sich
   * nichts, bis ausgerollt wird.
   */
  const [gespeichert, setGespeichert] = useState<{ anzahl: number; retire: boolean } | null>(null);

  const [lesegrund, setLesegrund] = useState<string | null>(null);
  const [gelesen, setGelesen] = useState<number | null>(null);

  const einlesen = useMutation({
    mutationFn: () => instances.vorhandeneTenants(instanceId),
    onSuccess: (ergebnis) => {
      setLesegrund(ergebnis.grund ?? null);
      setGelesen(ergebnis.mandanten.length || null);
      if (!ergebnis.mandanten.length) return;
      setRows(
        ergebnis.mandanten.map((m, i) => ({
          key: `gelesen-${i}`,
          code: m.code,
          name: m.name,
          isPrimary: m.isPrimary,
        })),
      );
      setDirty(true);
    },
  });

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-10 rounded-lg bg-slate-100" />
        ))}
      </div>
    );
  }

  if (!data?.supported) {
    return (
      <div className="rounded-xl border border-slate-200 bg-surface p-5 text-sm text-slate-500">
        Für dieses Produkt sind im Compose-Template keine Mandanten vorgesehen — bei
        open.task etwa ist eine Instanz der Mandant.
      </div>
    );
  }

  const change = (key: string, patch: Partial<Row>) => {
    setDirty(true);
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const makePrimary = (key: string) => {
    setDirty(true);
    setRows((rs) => rs.map((r) => ({ ...r, isPrimary: r.key === key })));
  };

  const add = () => {
    setDirty(true);
    setRows((rs) => [
      ...rs,
      { key: `neu-${nextKey++}`, code: '', name: '', isPrimary: rs.length === 0 },
    ]);
  };

  const remove = (key: string) => {
    setDirty(true);
    setRows((rs) => {
      const left = rs.filter((r) => r.key !== key);
      // Der primäre darf nicht mitgelöscht werden, ohne dass ein anderer
      // nachrückt — sonst startet die Oberfläche nirgends.
      if (left.length && !left.some((r) => r.isPrimary)) left[0].isPrimary = true;
      return left;
    });
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-surface">
        <div className="border-b border-slate-100 px-5 py-4">
          <h3 className="text-sm font-semibold text-slate-700">Startmandanten</h3>
          <p className="mt-0.5 text-xs text-slate-400">
            Was hier steht, legt die Instanz beim Start an — samt einer Gruppe mit vollen
            Rechten und beiden Administratoren darin. Wirkt beim nächsten Ausrollen.
          </p>
        </div>

        <div className="divide-y divide-slate-100">
          {rows.length === 0 && (
            <p className="px-5 py-4 text-sm italic text-slate-400">
              Kein Mandant hinterlegt — die Instanz startet dann so, wie das Abbild es
              vorgibt.
            </p>
          )}
          {rows.map((r) => (
            <div key={r.key} className="flex flex-wrap items-center gap-3 px-5 py-3">
              <button
                type="button"
                onClick={() => makePrimary(r.key)}
                title={r.isPrimary ? 'Primärer Mandant' : 'Als primären Mandanten setzen'}
                className={
                  r.isPrimary
                    ? 'text-amber-500'
                    : 'text-slate-300 hover:text-amber-400'
                }
              >
                <Star size={16} fill={r.isPrimary ? 'currentColor' : 'none'} />
              </button>
              <input
                value={r.name}
                placeholder={t('tenants.displayNameExample')}
                onChange={(e) => change(r.key, { name: e.target.value })}
                className="min-w-48 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <input
                value={r.code}
                placeholder={t('tenants.codeHint')}
                onChange={(e) => change(r.key, { code: e.target.value })}
                className="min-w-40 flex-1 rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <button
                type="button"
                onClick={() => remove(r.key)}
                className="text-slate-400 hover:text-red-600"
                title="Entfernen"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>

        {/* Was das Speichern ergeben hat.
            Verschwindet, sobald wieder etwas geaendert wird — eine grüne
            Bestaetigung neben ungespeicherten Aenderungen waere eine Luege. */}
        {gespeichert && !dirty && (
          <div className="mx-5 mb-3 flex items-start gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
            <Check size={13} className="mt-0.5 shrink-0" />
            <span>
              {t('tenantsSave.done', { count: gespeichert.anzahl })}{' '}
              <span className="font-medium">{t('tenantsSave.deployNeeded')}</span>
              {gespeichert.retire && (
                <span className="mt-0.5 block text-emerald-700">
                  {t('tenantsSave.retireNote')}
                </span>
              )}
            </span>
          </div>
        )}

        {/* Was das Einlesen ergeben hat. Ein Grund ist keine Störung — eine
            Instanz, die gerade nicht läuft, soll erklärt und nicht verschwiegen
            werden. */}
        {(lesegrund || gelesen) && (
          <div
            className={`mx-5 mb-3 rounded-lg px-3 py-2 text-xs ${
              lesegrund ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-800'
            }`}
          >
            {lesegrund ?? (
              <>
                {t('tenantsRead.found', { count: gelesen ?? 0 })}{' '}
                {t('tenantsRead.replaced')}
              </>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-5 py-4">
          <button
            type="button"
            onClick={add}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            <Plus size={14} /> Mandant
          </button>
          <button
            type="button"
            title={t('tenantsRead.hint')}
            disabled={einlesen.isPending}
            onClick={() => einlesen.mutate()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <Download size={14} />
            {einlesen.isPending ? t('tenantsRead.loading') : t('tenantsRead.load')}
          </button>
          <div className="flex items-center gap-3">
            {dirty && <span className="text-xs text-amber-600">{t('common.unsaved')}</span>}
            <button
              type="button"
              disabled={save.isPending}
              onClick={() => save.mutate()}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700 disabled:opacity-60"
            >
              {save.isPending ? 'Speichern…' : 'Speichern'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <ModulFreischaltung instanceId={instanceId} state={data} />


      {/* Mitgelieferte Mandanten. Bewusst ein eigener Schalter und aus per
          Vorgabe: bei einer bestehenden Installation kann daran längst etwas
          hängen. */}
      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-surface p-5">
        <input
          type="checkbox"
          checked={retire}
          onChange={(e) => {
            setDirty(true);
            setRetire(e.target.checked);
          }}
          className="mt-0.5"
        />
        <span className="text-sm">
          <span className="font-medium text-slate-700">
            Mitgelieferte Mandanten auf inaktiv setzen
          </span>
          <span className="mt-0.5 block text-xs text-slate-500">
            Betrifft alles, was nicht oben steht — bei ojfm „Standard", „Mandant 2" und
            „Bärenreiter Verlag", die aus der Migrationskette kommen. Inaktiv, nicht
            gelöscht. Bei einer <strong>bestehenden</strong> Installation vorher prüfen,
            ob etwas daran hängt.
          </span>
        </span>
      </label>

      {data.preview && (
        <div className="rounded-xl border border-slate-200 bg-surface p-5">
          <h3 className="mb-1 text-sm font-semibold text-slate-700">
            Was in die <code className="font-mono text-xs">.env</code> geschrieben wird
          </h3>
          <p className="mb-3 text-xs text-slate-400">
            Als <code className="font-mono">JUNI_TENANTS_B64</code>, base64-kodiert — ein
            Firmenname darf ein <code>#</code> enthalten, und daran scheitert der
            dotenv-Parser. Hier lesbar.
          </p>
          <pre className="overflow-x-auto rounded bg-konsole p-4 font-mono text-xs text-konsole-gruen">
            {JSON.stringify(data.preview, null, 2)}
          </pre>
          <div className="mt-3 flex items-start gap-2 text-xs text-slate-500">
            <Info size={14} className="mt-0.5 shrink-0" />
            <span>
              Unser Konto steht mit <code className="font-mono">"tenants": "*"</code> drin
              und gilt für alle Mandanten — es muss auch dann noch funktionieren, wenn der
              Kunde sich selbst aussperrt. Das Kundenkonto bekommt ausdrücklich nur die
              Mandanten dieser Instanz.
              {(() => {
                const deny = data.preview?.administrators.find((a) => a.denyModules?.length)
                  ?.denyModules;
                if (!deny?.length) return null;
                return (
                  <>
                    {' '}
                    Unter <code className="font-mono">denyModules</code> steht, was ihm
                    trotz Administratorrolle verwehrt bleibt, weil es uns gehört —
                    aktuell {deny.map((m) => <code key={m} className="mx-0.5 font-mono">{m}</code>)}
                    . Gepflegt am Compose-Template des Produkts.
                  </>
                );
              })()}
            </span>
          </div>
        </div>
      )}

      <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <span>
          Die Auswertung dieser Werte steht in ojfm noch aus (siehe
          <code className="mx-1 font-mono">UEBERGABE-RECHTE-UND-MANDANTEN.md</code>).
          Bis dahin schreibt der Deploy sie zwar in die <code className="font-mono">.env</code>,
          die Instanz ignoriert sie aber — Mandanten und Rechte müssen bis dahin von Hand
          gesetzt werden.
        </span>
      </div>
    </div>
  );
}
